import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Alert from '@mui/material/Alert';
import { DatePicker } from '@mui/x-date-pickers';
import { enqueueSnackbar } from 'notistack';
import axiosInstance from 'src/api/axiosInstance';
import { encryptData } from 'src/utils/Crypto';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { useBoolean } from 'src/hooks/use-boolean';

export default function ReplaceAdminDialog({
  open,
  onClose,
  currentUser,
  targetRole, // { userSubscrSiteRoleId, subscrSiteName }
  onSuccess,
}) {
  const [userArr, setUserArr] = useState([]);
  const [replaceUser, setReplaceUser] = useState(null);
  const [replaceStartDate, setReplaceStartDate] = useState(new Date());
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem('token');
  const confirmDialog = useBoolean();

  useEffect(() => {
    if (!open) {
      setReplaceUser(null);
      setReplaceStartDate(new Date());
      return;
    }

    const fetchUsers = async () => {
      try {
        const response = await axiosInstance.get(`/rest/userrole/active-users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const now = new Date();
        const filteredAndSortedUsers = response.data
          .filter((u) => !u.endDate || new Date(u.endDate) > now)
          .sort((a, b) => {
            if (a.userName < b.userName) return -1;
            if (a.userName > b.userName) return 1;
            return 0;
          });
        setUserArr(filteredAndSortedUsers);
      } catch (error) {
        console.error('Failed to fetch active users', error);
        setUserArr([]);
      }
    };
    fetchUsers();
  }, [open, token]);

  const employeeOptions = useMemo(
    () => userArr.filter((u) => u.isEmployee === true && u.userId !== currentUser?.userId),
    [userArr, currentUser]
  );

  const handleConfirm = async () => {
    if (!replaceUser || !targetRole) {
      enqueueSnackbar('Please select a user to replace with', { variant: 'warning' });
      return;
    }
    
    confirmDialog.onFalse();
    setSubmitting(true);
    try {
      // 1. Check if the replacement user is ALREADY an Admin for this site
      const checkRes = await axiosInstance.post(
        `/rest/userrole/getbyuser`,
        { userId: replaceUser.userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const existingRoles = checkRes.data || [];
      const alreadyHasRole = existingRoles.some((r) => {
        const isActive = !r.endDate || new Date(r.endDate) > new Date();
        const roleName = r.roleName?.trim();
        return r.subscrSiteName === targetRole.subscrSiteName && roleName === 'Admin' && isActive;
      });

      if (alreadyHasRole) {
        enqueueSnackbar(`Cannot replace: ${replaceUser.userName} is already an active Admin at ${targetRole.subscrSiteName}.`, { variant: 'error' });
        setSubmitting(false);
        return;
      }

      // 2. Proceed with the replace
      const payload = {
        userSubscrSiteRoleId: targetRole.userSubscrSiteRoleId,
        newUserId: encryptData(replaceUser.userId),
        startDate: replaceStartDate,
      };
      await axiosInstance.post(`/rest/userrole/replaceadmin`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      enqueueSnackbar('Admin role replaced successfully', { variant: 'success' });
      if (onSuccess) {
        await onSuccess();
      }
      onClose();
    } catch (error) {
      const message = error?.response?.data?.message || 'Error replacing Admin';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>Replace Admin</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
          This will end the current Admin assignment for <b>{currentUser?.userName}</b> at{' '}
          <b>{targetRole?.subscrSiteName}</b> and assign the Admin role to the selected user instead.
        </Alert>
        <Stack spacing={2}>
          <Autocomplete
            options={employeeOptions}
            getOptionLabel={(option) => option.userName || ''}
            value={replaceUser}
            onChange={(event, newValue) => setReplaceUser(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  <span>
                    New Admin<span style={{ color: 'red' }}>*</span>
                  </span>
                }
              />
            )}
          />
          <TextField label="Site" value={targetRole?.subscrSiteName || ''} disabled fullWidth />
          <DatePicker
            label="Start date"
            value={replaceStartDate}
            onChange={(newValue) => setReplaceStartDate(newValue)}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={confirmDialog.onTrue}
          disabled={!replaceUser || submitting}
        >
          Confirm
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={confirmDialog.value}
        onClose={confirmDialog.onFalse}
        title="Confirm Replacement"
        content="Are you sure you want to replace this Admin? This action will transfer the role immediately based on the selected start date."
        action={
          <Button variant="contained" color="primary" onClick={handleConfirm}>
            Replace
          </Button>
        }
      />
    </Dialog>
  );
}

ReplaceAdminDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentUser: PropTypes.object,
  targetRole: PropTypes.shape({
    userSubscrSiteRoleId: PropTypes.string,
    subscrSiteName: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
};
